import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Button, Container, Segment, Table } from 'semantic-ui-react';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _merge from 'lodash/merge';
import _set from 'lodash/set';
import { useSelector } from 'react-redux';

import ConfirmModal from 'components/common/ConfirmModal';
import { getGoogleLocationProviderSetting } from 'components/modules/business-profile/services/provider-service';
import { getLocationDisplay } from 'components/modules/location/services';
import { isAdminUser } from 'libs/auth';
import { multiCaseGet } from 'libs/format';
import { selectBusinessLocations } from 'selectors';
import { selectUser } from 'selectors/user';

import { SETTING_LOGICAL_ORDER, SETTING_TYPES } from './AccountSettings.constants';
import { GoogleLocationPropType, LocationSettingsPropType } from './AccountSettings.types';
import {
  getGoogleLocationLabel,
  getInternalLocationLabel,
  getLocationInfoModalDetails,
  getLocationSyncSettings,
} from './AccountSettings.utils';
import LocationInfoModal from './components/LocationInfoModal';
import PullSettings from './components/PullSettings/PullSettings';
import PushSettings from './components/PushSettings/PushSettings';

import './AccountSettings.scss';

const propTypes = {
  loading: PropTypes.bool,
  associatedLocations: PropTypes.arrayOf(GoogleLocationPropType),
  locationSettings: LocationSettingsPropType,
  onDisconnect: PropTypes.func.isRequired,
  onDisconnectLocation: PropTypes.func,
  onLocationSettingsChange: PropTypes.func.isRequired,
  excludedSettings: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  loading: false,
  associatedLocations: [],
  locationSettings: {},
  excludedSettings: [],
  onDisconnectLocation: null,
};

function AccountSettings({
  loading,
  onDisconnect,
  onDisconnectLocation,
  associatedLocations,
  locationSettings,
  onLocationSettingsChange,
  excludedSettings,
}) {
  const isAdmin = isAdminUser(useSelector(selectUser));
  const fishermanLocations = useSelector((state) =>
    selectBusinessLocations(state, { camelCase: true }),
  );
  const [pendingDisconnect, setPendingDisconnect] = useState(null);
  const [showDisconnectAccountConfirm, setShowDisconnectAccountConfirm] = useState(false);
  const [locationInfoModal, setLocationInfoModal] = useState(null);

  function getSyncSettings(location) {
    return getLocationSyncSettings(locationSettings, location);
  }

  function onSettingsChange(location, settingName, settingType, newPartialSettingsForType) {
    const fullSyncSettings = _cloneDeep(getLocationSyncSettings(locationSettings, location));
    const newSyncSettingsForType = _merge(
      multiCaseGet(fullSyncSettings, `${settingName}.${settingType}.settings`),
      newPartialSettingsForType,
    );

    const isPull = settingType === SETTING_TYPES.PULL;
    const isPush = settingType === SETTING_TYPES.PUSH;

    const otherSettingType = isPull ? SETTING_TYPES.PUSH : SETTING_TYPES.PULL;
    const otherSupported = multiCaseGet(
      fullSyncSettings,
      `${settingName}.${otherSettingType}.supported`,
    );

    const settingForTypeEnabled = _get(newSyncSettingsForType, 'enabled');
    if (isPull) {
      // If pull is disabled, push is disabled too
      if (!settingForTypeEnabled && otherSupported) {
        _set(
          fullSyncSettings,
          `${settingName}.${otherSettingType}.settings.enabled`,
          settingForTypeEnabled,
        );
      }
    } else if (isPush) {
      // If push is enabled, pull is enabled too
      if (settingForTypeEnabled && otherSupported) {
        _set(
          fullSyncSettings,
          `${settingName}.${otherSettingType}.settings.enabled`,
          settingForTypeEnabled,
        );
      }
    }

    _set(fullSyncSettings, `${settingName}.${settingType}.settings`, newSyncSettingsForType);
    onLocationSettingsChange(location, fullSyncSettings);
  }

  function onPullSettingChange(location, settingName, newPartialSettings) {
    onSettingsChange(location, settingName, SETTING_TYPES.PULL, newPartialSettings);
  }

  function onPushSettingChange(location, settingName, newPartialSettings) {
    onSettingsChange(location, settingName, SETTING_TYPES.PUSH, newPartialSettings);
  }

  return (
    <Segment attached="bottom" loading={loading} className="account-settings">
      {associatedLocations !== null &&
        associatedLocations.map((location) => {
          const locationId = multiCaseGet(location, 'locationId');
          const locationTitle = multiCaseGet(location, 'title');

          const settings = getSyncSettings(location);
          let names = Object.keys(settings).sort(
            (settingA, settingB) =>
              SETTING_LOGICAL_ORDER.indexOf(settingA) - SETTING_LOGICAL_ORDER.indexOf(settingB),
          );

          if (!_isEmpty(excludedSettings)) {
            names = names.filter((name) => !excludedSettings.includes(name));
          }

          const locationSetting = getGoogleLocationProviderSetting(locationSettings, location);
          const internalLocationId = multiCaseGet(
            locationSetting,
            'serviceData.fishermanLocationId',
          );
          const fishermanLocation = fishermanLocations.find(
            (item) => String(item.id) === String(internalLocationId),
          );
          const internalLocationName = fishermanLocation
            ? getLocationDisplay(fishermanLocation)
            : locationTitle;
          const internalLocationLabel = getInternalLocationLabel(
            fishermanLocation,
            internalLocationId,
          );
          const googleLocationLabel = getGoogleLocationLabel(location);

          return (
            <div className="location-settings-block" key={locationId}>
              <Table>
                <Table.Header>
                  <Table.HeaderCell className="location-settings-header">
                    <div className="location-settings-header-line">
                      <span>
                        <strong>Internal Location:</strong> {internalLocationLabel}
                      </span>
                      {isAdmin && (
                        <Button
                          basic
                          icon="info circle"
                          className="location-info-button"
                          aria-label="View internal location details"
                          onClick={() =>
                            setLocationInfoModal({
                              type: 'internal',
                              fishermanLocation,
                              internalLocationId,
                            })
                          }
                        />
                      )}
                    </div>
                    <div className="location-settings-header-line">
                      <span>
                        <strong>Google Location:</strong> {googleLocationLabel}
                      </span>
                      {isAdmin && (
                        <Button
                          basic
                          icon="info circle"
                          className="location-info-button"
                          aria-label="View Google location details"
                          onClick={() =>
                            setLocationInfoModal({
                              type: 'google',
                              gmbLocation: location,
                            })
                          }
                        />
                      )}
                    </div>
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Enabled</Table.HeaderCell>
                </Table.Header>
                <Table.Body>
                  {names.map((name) => {
                    const setting = settings[name];

                    return (
                      <React.Fragment key={name}>
                        <PullSettings
                          setting={setting}
                          name={name}
                          location={location}
                          onSettingChange={(newSettings) =>
                            onPullSettingChange(location, name, newSettings)
                          }
                        />
                        <PushSettings
                          setting={setting}
                          name={name}
                          location={location}
                          onSettingChange={(newSettings) =>
                            onPushSettingChange(location, name, newSettings)
                          }
                        />
                      </React.Fragment>
                    );
                  })}
                </Table.Body>
              </Table>
              {isAdmin && internalLocationId != null && (
                <div className="disconnect-location-action">
                  <Button
                    negative
                    content="Disconnect Location"
                    onClick={() =>
                      setPendingDisconnect({
                        internalLocationId,
                        internalLocationName,
                        googleTitle: locationTitle,
                      })
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      <LocationInfoModal
        open={Boolean(locationInfoModal)}
        header={locationInfoModal?.type === 'google' ? 'Google Location' : 'Internal Location'}
        details={getLocationInfoModalDetails(locationInfoModal)}
        onClose={() => setLocationInfoModal(null)}
      />
      <ConfirmModal
        header="Disconnect Location"
        content={
          pendingDisconnect
            ? `Disconnect ${pendingDisconnect.internalLocationName} from the Google listing ${pendingDisconnect.googleTitle}? The Google account and other locations stay connected. Related imported data for this location will be removed.`
            : ''
        }
        open={Boolean(pendingDisconnect)}
        onCancel={() => setPendingDisconnect(null)}
        onConfirm={() => {
          if (pendingDisconnect) {
            onDisconnectLocation(pendingDisconnect.internalLocationId);
          }
          setPendingDisconnect(null);
        }}
        confirmButton="Disconnect Location"
        cancelButton="Cancel"
      />
      <ConfirmModal
        header="Disconnect Account"
        content="Disconnect this Google Business Profile account? This removes OAuth and all location links for this business."
        open={showDisconnectAccountConfirm}
        onCancel={() => setShowDisconnectAccountConfirm(false)}
        onConfirm={() => {
          onDisconnect();
          setShowDisconnectAccountConfirm(false);
        }}
        confirmButton="Disconnect Account"
        cancelButton="Cancel"
      />
      <Container textAlign="center" className="disconnect-account-action">
        <Button
          negative
          content="Disconnect Account"
          onClick={() => setShowDisconnectAccountConfirm(true)}
        />
      </Container>
    </Segment>
  );
}

AccountSettings.propTypes = propTypes;
AccountSettings.defaultProps = defaultProps;

export default AccountSettings;
